/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { Item, Section, useAsyncList, type Key } from 'react-stately';

import { SearchActions, Autosuggest, Icon } from 'sarsaparilla';

import { useAuthState } from 'shared-ui/auth';

import { getTargetUri } from '../../maps/helpers/mapHelper';
import { useTypedDispatch } from '../../redux';

import { LocationSuggestion } from './LocationSuggestion';

import type {
    HistorySuggestion,
    LocationSuggestionOptionType,
    Suggestion,
} from './LocationSuggestion.types';
import {
    isEntityTypeSuggestion,
    isHistorySuggestion,
    isInventorySuggestion,
} from './LocationSuggestion.utils';
import { isSectionOption, type SectionOption } from './SearchAutoSuggest.types';
import {
    containsEmail,
    getSearchSuggestions,
    getSuggestionDisplayValue,
    TEXT_CLEAR_SEARCH_HIST,
    TEXT_SEARCH_NEAR_ME,
} from './SearchAutosuggest.utils';

import styles from './SearchAutosuggest.module.scss';

interface SearchAutosuggestProps {
    buildGATrackingObject?: (
        suggestion: Suggestion | null,
        isNearMe?: boolean
    ) => { clickTagCategory: string; clickTagAction: string; clickTagLabel: string };
    clickTagAction?: string;
    clickTagCategory?: string;
    clickTagLabel?: string;
    contentTypes?: string[];
    defaultEventCategory?: string;
    disableSearchOnSelect?: boolean;
    geocoder?: boolean;
    inventoryTypes?: string[];
    placeholder?: string;
    popupClassName?: string;
    searchOnSelectTypes?: string[];
    size?: 'md' | 'lg';
    queryCriteriaPropertyName?: string;
    onRunSearch?: ({
        searchText,
        suggestion,
        searchHistory,
    }: {
        searchText: string;
        suggestion: Suggestion | null;
        searchHistory: HistorySuggestion[];
    }) => void;
    id: string;
    shouldPopoverFlip?: boolean;
    updateOnScroll?: boolean;
}

export function SearchAutosuggest(props: SearchAutosuggestProps) {
    const {
        contentTypes = null,
        disableSearchOnSelect = false,
        defaultEventCategory = 'Global Navigation',
        geocoder = true,
        inventoryTypes = null,
        placeholder = 'What are you looking for?',
        popupClassName = styles.searchAutosuggestPopup,
        queryCriteriaPropertyName = 'what',
        searchOnSelectTypes = [],
        size = 'md',
        buildGATrackingObject,
        clickTagAction,
        clickTagCategory,
        clickTagLabel,
        id,
        onRunSearch,
        shouldPopoverFlip = true,
        updateOnScroll = false,
    } = props;

    const dispatch = useTypedDispatch();
    const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
    const historyLastFetchedRef = React.useRef(0);

    const updateHistoryLastFetched = () => {
        historyLastFetchedRef.current = Date.now();
    };

    // Using selectedSuggestionOption to handle possible race conditions between
    // onSelectionChange & onKeyUp/Down when keyboard user selects suggestion
    // via 'Enter' key, it triggers both
    const selectedSuggestionOption = React.useRef<LocationSuggestionOptionType | null>(
        null
    );
    const { account } = useAuthState();

    const hasClickTagProps = clickTagAction && clickTagCategory && clickTagLabel;

    const searchNearMe = () => {
        const trEvent = {
            category: 'Search',
            action: 'Select Search Suggestion Near Me',
            label: 'Near Me',
        };

        if (hasClickTagProps) {
            trEvent.category = clickTagCategory;
            trEvent.action = clickTagAction;
            trEvent.label = clickTagLabel;
        }

        if (buildGATrackingObject) {
            const gaTrackingObject = buildGATrackingObject(null, true);

            trEvent.category = gaTrackingObject.clickTagCategory;
            trEvent.action = gaTrackingObject.clickTagAction;
            trEvent.label = gaTrackingObject.clickTagLabel;
        }

        SearchActions.trackRunSearch(trEvent, () => {
            dispatch(SearchActions.submitSimpleSearch());
        });
    };

    const searchNearMeOption: LocationSuggestionOptionType = {
        id: crypto.randomUUID(),
        text: TEXT_SEARCH_NEAR_ME,
        name: TEXT_SEARCH_NEAR_ME,
    };

    // execute search request to navigate to selected item or navigate to search results
    const runSearch = async (
        suggestionOption: LocationSuggestionOptionType | null,
        searchText: string
    ) => {
        let eventCategory = defaultEventCategory;
        let eventAction = 'Submit Search';
        let eventLabel = searchText;
        if (suggestionOption) {
            eventAction = 'Select Search Suggestion';
            if (isHistorySuggestion(suggestionOption) && suggestionOption.last_used) {
                eventAction = 'Select Search History Suggestion';
            }
            eventLabel = suggestionOption.name || suggestionOption.text;
        }

        if (hasClickTagProps) {
            eventCategory = clickTagCategory;
            eventAction = clickTagAction;
            eventLabel = clickTagLabel;
        }

        if (buildGATrackingObject) {
            const gaTrackingObject = buildGATrackingObject(suggestionOption);

            if (
                gaTrackingObject.clickTagCategory &&
                gaTrackingObject.clickTagAction &&
                gaTrackingObject.clickTagLabel
            ) {
                eventCategory = gaTrackingObject.clickTagCategory;
                eventAction = gaTrackingObject.clickTagAction;
                eventLabel = gaTrackingObject.clickTagLabel;
            }
        }

        if (containsEmail(eventLabel)) {
            eventLabel = '[Redacted Email]';
        }
        const event = {
            category: eventCategory,
            action: eventAction,
            label: eventLabel,
        };

        // Commenting this out for now - I think we can just use the "simple cache"
        // const searchHistory = await SearchActions.loadSearchHistoryItems(
        //     updateHistoryLastFetched
        // );

        const isReservable =
            suggestionOption &&
            (isHistorySuggestion(suggestionOption) ||
                isInventorySuggestion(suggestionOption)) &&
            suggestionOption.reservable;
        const hasTargetedEntityTypes =
            suggestionOption &&
            isEntityTypeSuggestion(suggestionOption) &&
            (suggestionOption.entity_type === 'recarea' ||
                suggestionOption.entity_type === 'kb' ||
                suggestionOption.entity_type === 'cms' ||
                suggestionOption.entity_type === 'page' ||
                suggestionOption.entity_type === 'pass');
        if (isReservable || hasTargetedEntityTypes) {
            try {
                await SearchActions.addSearchHistoryItem({
                    searchHistory: undefined,
                    rawSearchText: searchText,
                    suggestion: suggestionOption,
                });
            } catch (e) {
                // ignore errors
                // eslint-disable-next-line no-console
                console.log('error saving history', e);
            }

            const url = getTargetUri(suggestionOption);
            SearchActions.trackRunSearch(event, () => {
                window.location.assign(url);
            });
        } else if (onRunSearch) {
            SearchActions.trackRunSearch(event, () => {
                onRunSearch({
                    searchText,
                    suggestion: suggestionOption,
                    searchHistory: [],
                });
            });
        } else {
            try {
                await SearchActions.addSearchHistoryItem({
                    searchHistory: undefined,
                    rawSearchText: searchText,
                    suggestion: suggestionOption,
                });
            } catch (e) {
                // ignore errors
                // eslint-disable-next-line no-console
                console.log('error saving history', e);
            }

            SearchActions.trackRunSearch(event, () => {
                dispatch(
                    SearchActions.submitSimpleSearch(searchText, null, suggestionOption)
                );
            });
        }
    };

    const handleSuggestionClicked = (suggestionOption: LocationSuggestionOptionType) => {
        selectedSuggestionOption.current = null;
        const suggestionText = getSuggestionDisplayValue(suggestionOption);

        // Remove search session from localStorage because filter settings can
        // affect camping facility page display.
        try {
            window.localStorage.removeItem('r1s_search_session');
        } catch (error) {
            // do nothing
        }

        dispatch(
            SearchActions.updateSearchCriterias({
                selectedSuggestionOption: suggestionOption,
                [queryCriteriaPropertyName]: suggestionText,
            })
        );

        if (
            !disableSearchOnSelect ||
            (isEntityTypeSuggestion(suggestionOption) &&
                searchOnSelectTypes.includes(suggestionOption?.entity_type ?? ''))
        ) {
            // run search
            runSearch(suggestionOption, suggestionText);
        }
    };

    const createSuggestionOption = (s: Suggestion) => {
        const sOption: LocationSuggestionOptionType = {
            ...s,
            id: crypto.randomUUID(),
        };

        return sOption;
    };

    const loadSearchHistory = async () => {
        try {
            const history = (
                await SearchActions.loadSearchHistoryItems(updateHistoryLastFetched)
            ).filter((s: HistorySuggestion) => !!s.text);

            const historyOption: (SectionOption | LocationSuggestionOptionType)[] = [
                searchNearMeOption,
            ];

            if (history.length) {
                const recentSearchesOption = {
                    id: crypto.randomUUID(),
                    text: 'Recent Searches',
                    children: history.map(createSuggestionOption),
                };

                // Clear Button is a part of the Recent Searches list
                recentSearchesOption.children.push({
                    id: crypto.randomUUID(),
                    text: TEXT_CLEAR_SEARCH_HIST,
                    name: TEXT_CLEAR_SEARCH_HIST,
                    customRender: (
                        <div className={styles.searchSuggestion}>
                            <div className={styles.clearSearchHistory}>
                                <Icon iconName="loop" size="sm" />
                                <span>{TEXT_CLEAR_SEARCH_HIST}</span>
                            </div>
                        </div>
                    ),
                });

                historyOption.push(recentSearchesOption);
            }

            return historyOption;
        } catch (e: unknown) {
            // ignore errors
            // eslint-disable-next-line no-console
            console.error('error loading history', e);
            return [];
        }
    };

    const list = useAsyncList<SectionOption | LocationSuggestionOptionType>({
        async load({ signal, filterText }) {
            if (!filterText) {
                const historyItems = await loadSearchHistory();

                return {
                    items: historyItems,
                };
            }

            const params = {
                q: filterText,
                geocoder,
                type: contentTypes,
                inventory_type: inventoryTypes,
            };

            const searchSuggestions = await getSearchSuggestions(signal, params);

            return {
                items: [
                    {
                        text: 'Recreation Areas, Facilities, Tours, Trails',
                        id: crypto.randomUUID(),
                        children: (searchSuggestions?.inventory_suggestions ?? []).map(
                            createSuggestionOption
                        ),
                    },
                    {
                        text: 'Locations, City, State, Zip Code',
                        id: crypto.randomUUID(),
                        children: (searchSuggestions?.suggestions ?? []).map(
                            createSuggestionOption
                        ),
                    },
                    {
                        text: 'Help Topics, Frequently Asked Questions, Articles',
                        id: crypto.randomUUID(),
                        children: (searchSuggestions?.content_suggestions ?? []).map(
                            createSuggestionOption
                        ),
                    },
                ].filter((o) => o.children.length),
            };
        },
    });

    async function clearHistory() {
        try {
            await SearchActions.deleteSearchHistoryItems();
        } catch (e) {
            // ignore errors
            // eslint-disable-next-line no-console
            console.log('error deleting history', e);
        }
        selectedSuggestionOption.current = null;
        list.reload();

        const updateParams = {
            [queryCriteriaPropertyName]: '',
        };
        dispatch(SearchActions.updateSearchCriterias(updateParams));
    }

    React.useEffect(() => {
        // Reloading history when user logs in / out and on mount
        if (account?.account_id && historyLastFetchedRef.current > 0) {
            list.reload();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account?.account_id]);

    const findSuggestionOptionByKey = (
        key: string
    ): LocationSuggestionOptionType | null => {
        for (let i = 0; i < list.items.length; i += 1) {
            const option = list.items[i];
            if (isSectionOption(option)) {
                const suggestionOption = option.children.find((o) => o.id === key);
                if (suggestionOption) {
                    return suggestionOption;
                }
            } else if (option.id === key) {
                return option;
            }
        }

        return null;
    };

    const clearInputFields = () => {
        list.setFilterText('');
        setSelectedKey(null);
        selectedSuggestionOption.current = null;
    };

    // when auto suggestion was selected somehow using nav
    const handleSelectionChange = async (key: Key | null) => {
        if (!key) {
            return;
        }

        const suggestionOption = findSuggestionOptionByKey(key as string);
        if (!suggestionOption) return;

        setSelectedKey(suggestionOption.id);
        selectedSuggestionOption.current = suggestionOption;

        // No need to set filter text if one of these options since,
        // it'll be empty and for some reason setting it to empty
        // messes up the usage of onClick & the onPressDown in the ListBox Option
        if (
            suggestionOption.text !== TEXT_CLEAR_SEARCH_HIST &&
            suggestionOption.text !== TEXT_SEARCH_NEAR_ME
        ) {
            const suggestionText = getSuggestionDisplayValue(suggestionOption);
            list.setFilterText(suggestionText);
            const updateParams = {
                [queryCriteriaPropertyName]: suggestionText,
            };
            dispatch(SearchActions.updateSearchCriterias(updateParams));
        }
    };

    const handleSelect = (key: Key) => {
        const suggestionOption = findSuggestionOptionByKey(key as string);
        if (!suggestionOption) {
            return;
        }

        if (suggestionOption.text === TEXT_SEARCH_NEAR_ME) {
            searchNearMe();
        } else if (suggestionOption.text === TEXT_CLEAR_SEARCH_HIST) {
            clearHistory();
        } else {
            handleSuggestionClicked(suggestionOption);
        }
    };

    // This handles the redirect when users try to select an autosuggestion
    const handleKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            if (!selectedSuggestionOption.current && list.filterText) {
                runSearch(null, list.filterText);
            }
        }
    };

    const handleClear = (): void => {
        clearInputFields();
        const updateParams = {
            [queryCriteriaPropertyName]: '',
        };
        dispatch(SearchActions.updateSearchCriterias(updateParams));
    };

    const handleInputChange = (value: string): void => {
        list.setFilterText(value);
        if (selectedKey) {
            setSelectedKey(null);
            selectedSuggestionOption.current = null;
        }
        const updateParams = {
            [queryCriteriaPropertyName]: value,
        };
        dispatch(SearchActions.updateSearchCriterias(updateParams));
    };

    return (
        <div>
            <Autosuggest
                id={id}
                label={`Search ${process.env.SITE_NAME}`}
                items={list.items}
                inputValue={list.filterText}
                isLabelVisible={false}
                menuTrigger="focus"
                placeholder={placeholder}
                onClear={handleClear}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSelectionChange={handleSelectionChange}
                onSelect={handleSelect}
                popupClassName={popupClassName}
                selectedKey={selectedKey}
                size={size}
                allowsCustomValue
                allowsEmptyCollection
                updateOnScroll={updateOnScroll}
                shouldPopoverFlip={shouldPopoverFlip}
            >
                {(option) => {
                    return isSectionOption(option) ? (
                        <Section
                            title={option.text}
                            key={option.id}
                            items={option.children}
                        >
                            {(item: LocationSuggestionOptionType) => (
                                <Item
                                    key={item.id}
                                    textValue={getSuggestionDisplayValue(item)}
                                >
                                    <LocationSuggestion
                                        suggestion={item}
                                        searchText={list.filterText}
                                    />
                                </Item>
                            )}
                        </Section>
                    ) : (
                        <Item
                            key={option.id}
                            textValue={getSuggestionDisplayValue(option)}
                        >
                            <LocationSuggestion
                                suggestion={option}
                                searchText={list.filterText}
                            />
                        </Item>
                    );
                }}
            </Autosuggest>
        </div>
    );
}

// cSpell:ignore recarea, updateSearchCriterias
